import { ui, defaultLang, showDefaultLang } from "./ui";

/**
 * Extracts the language from a path.
 * @param path - The path to analyze.
 * @returns The language code or defaultLang if not found.
 */
export function extractLang(path: string): keyof typeof ui {
  const [firstSegment] = path.split("/").filter(Boolean);
  return firstSegment in ui ? (firstSegment as keyof typeof ui) : defaultLang;
}

/**
 * Gets the language from a URL object.
 * @param url - The URL object.
 * @returns The language code or defaultLang.
 */
export function getLangFromUrl(url: URL): keyof typeof ui {
  return extractLang(url.pathname);
}

/**
 * Retrieves the path after the locale/language segment.
 * @param pathname - The full pathname to analyze.
 * @returns The path without the locale prefix.
 */
export function getPathAfterLocale(pathname: string): string {
  const pathSegments = pathname.split("/").filter(Boolean);
  const lang = extractLang(pathname);

  if (lang !== defaultLang && pathSegments[0] === lang) {
    pathSegments.shift();
  }

  return pathSegments.join("/");
}

/**
 * Provides a translation function for a specific language.
 * @param lang - The language code.
 * @returns A function to retrieve translations.
 */
export function useTranslations(lang: keyof typeof ui) {
  return function t(path: string): string {
    const keys = path.split(".");
    let result = ui[lang];

    for (const key of keys) {
      result = result?.[key] ?? ui[defaultLang]?.[key];
      if (result === undefined) {
        console.error(`Translation not found for path: ${path}`);
        return "";
      }
    }

    return typeof result === "string" ? result : "";
  };
}

/**
 * Generates a localized path based on the language.
 * @param lang - The language code.
 * @returns A function to translate paths.
 */
export function useTranslatedPath(lang: keyof typeof ui) {
  return function translatePath(path: string, l: string = lang): string {
    return !showDefaultLang && l === defaultLang ? path : `/${l}/${path}`;
  };
}
